import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { ChevronDown } from "lucide-react";
import { useAlert } from "../MessageAlert/AlertContext";
import { AppDispatch, RootState } from "../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchAllUsers } from "../Redux/userSlice";
import { FormControl } from "@mui/material";
import {LOCALSTORAGE_KEYS,DEFAULT_UI_DICTIONARY } from '..//../utils/Constants';
import {getStorageItem, SetStorageItem} from '../../helpers/helpers'
import { setAccessTokenDirect } from '../../services/tokenManager'
import { useCappedOptions, LoadAllFooter } from './dropdownOptions'

const Dropdown = React.memo(({
  label,
  value,
  options,
  onChange,
  isMulti,
  required,
  disabled = false,
}: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filtering, the render cap and the "Load all" escape hatch are shared with
  // EmailLog.tsx and LogClarraDocsForm.tsx - see ./dropdownOptions. This list
  // used to render every match, which is what froze the pane on the ~50k
  // matter list; only the first MAX_VISIBLE_OPTIONS reach the DOM now.
  const {
    filteredOptions,
    visibleOptions,
    hiddenCount,
    showAllOptions,
    isExpanding,
    loadAll,
  } = useCappedOptions(options, search, isOpen);

  const handleRemove = (optionToRemove: any) => {

    const newValue = value.filter(
      (item: any) => item.value !== optionToRemove.value
    );
    onChange(newValue);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);
  return (
    <div className="" ref={dropdownRef}>
      <label className="block">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <div
          className={`border rounded-lg p-2 min-h-[10px] ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'cursor-text bg-white'
          }`}
          onClick={() => !disabled && setIsOpen(true)}
        >
          {isMulti ? (
            <div className="flex flex-wrap gap-1">
              {value?.map((v: any) => (
                <span
                  key={v?.value}
                  className="inline-flex items-center bg-blue-50 rounded-md px-2 m-1"
                >
                  <span className={`text-sm ${disabled ? 'text-gray-600' : 'text-gray-700'}`}>{v?.label}</span>
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(v);
                      }}
                      className="ml-1 text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      <X size={14} />
                    </button>
                  )}
                </span>
              ))}
              {!disabled && (
                <input
                  type="text"
                  className="outline-none flex-grow min-w-[100px] text-sm p-1"
                  placeholder="Type to search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsOpen(true)}
                />
              )}
            </div>
          ) : (
            <div className="flex items-center">
              {value ? (
                <>
                  <span className={`text-sm ${disabled ? 'text-gray-600' : 'text-gray-700'}`}>{value.label}</span>
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(null);
                      }}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={14} />
                    </button>
                  )}
                </>
              ) : (
                <>
                  {!disabled ? (
                    <input
                      type="text"
                      className="outline-none w-full text-sm"
                      placeholder="Type to search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onFocus={() => setIsOpen(true)}
                    />
                  ) : (
                    <span className="text-sm text-gray-500">Select an option...</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {isOpen && !disabled && (
          <div className="absolute z-10 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
            {visibleOptions.map((option) => (
              <div
                key={option.value}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  if (isMulti) {
                    // Avoid duplicates in the array

                    if (!value.find((item: any) => item.value === option.value)) {
                      onChange([...value, option]);
                    }
                  } else {
                    onChange(option);
                  }
                  setSearch("");
                  setIsOpen(false);
                }}
              >
                {option.label}
              </div>
            ))}
            <LoadAllFooter
              filteredCount={filteredOptions.length}
              hiddenCount={hiddenCount}
              showAllOptions={showAllOptions}
              isExpanding={isExpanding}
              onLoadAll={loadAll}
            />
            {filteredOptions.length === 0 && (
              <div className="px-4 py-2 text-xs text-gray-500">No matches</div>
            )}
          </div>
        )}
        {!disabled && (
          <ChevronDown
            className={`absolute right-2 top-3 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""
              }`}
            size={16}
          />
        )}
      </div>
    </div>
  );
});
Dropdown.displayName = "Dropdown";


const AutocompleteInput = React.memo(({
  label,
  value,
  options,
  onChange,
  required,
  placeholder,
  disabled = false,
}: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const wrapperRef = useRef(null);

  
  // ...existing code...
useEffect(() => {
  let option;
  if (typeof value === 'object' && value !== null) {
    option = value;
  } else if (typeof value === 'string') {
    option = options.find((opt) => opt.value === value || opt.label === value);
    if (!option && value) {
      option = { value: value, label: value };
    }
  }
  // Only update state if changed
  if (
    (option && (!selectedOption || selectedOption.value !== option.value)) ||
    (!option && selectedOption)
  ) {
    setSelectedOption(option);
    setSearchTerm(option ? option.label : "");
  }
  // eslint-disable-next-line
}, [value, options]);
// ...existing code...

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        if (selectedOption) {
          setSearchTerm(selectedOption.label);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedOption]);

 

  // Same shared capping/"Load all" behaviour as Dropdown above. The
  // timekeeper list is the one that can get long here.
  const {
    filteredOptions,
    visibleOptions,
    hiddenCount,
    showAllOptions,
    isExpanding,
    loadAll,
  } = useCappedOptions(options, searchTerm, isOpen);

  const handleSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm(option.label);
    setIsOpen(false);
    onChange(option.value);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            if (!disabled) {
              setSearchTerm(e.target.value);
              setIsOpen(true);
            }
          }}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`w-full p-1 border rounded pr-8 ${
            disabled
              ? 'bg-gray-100 cursor-not-allowed text-gray-600'
              : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
          }`}
        />
        {!disabled && (
          <ChevronDown
            className={`absolute right-2 top-2 text-gray-400 transition-transform ${isOpen ? "transform rotate-180" : ""
              }`}
            size={14}
          />
        )}
      </div>
      {isOpen && !disabled && filteredOptions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {visibleOptions.map((option) => (
            <div
              key={option.id}
              onClick={() => handleSelect(option)}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
            >
              {option.label}
            </div>
          ))}
          <LoadAllFooter
            filteredCount={filteredOptions.length}
            hiddenCount={hiddenCount}
            showAllOptions={showAllOptions}
            isExpanding={isExpanding}
            onLoadAll={loadAll}
          />
        </div>
      )}
    </div>
  );
});
AutocompleteInput.displayName = "AutocompleteInput";
const LogTimeslipForm = ({
  setOnlyTimeSlip,
  setCommunicationSave,
  data,
  handleSubmitNewTimeSlip,
  SetTimeSlipSettings,
  TimeSlipSetting,
  setErrors,
  errors,
  expanded,
  formData,
  setFormData,
}) => {
  const SavedData = data;
  const dispatch = useDispatch<AppDispatch>();
  const { uiDictionary} = useSelector((state: RootState) => state.labels);
  const matters = useSelector((state: RootState) => state.matters.matters);
  const tsettings = useSelector((state: RootState) => state.timeSlipSettings.settings);

  let mattersoptions = matters.map((matter) => ({
    value: matter.id,
    label: matter.matter + " (" + matter.number + ")",
  }));


  
  const activityCode = useSelector(
    (state: RootState) => state.activityCode.activityCode
  );
  let activityCodeOptions = activityCode
    .filter((activityCodeObj) => activityCodeObj && activityCodeObj.name)
    .map((activityCodeObj) => ({
      value: activityCodeObj.name,
      label: activityCodeObj.name,
    }));
  const taskCode = useSelector((state: RootState) => state.taskCode.taskCode);
  let taskCodeOptions = taskCode
    .filter((taskCodeObj) => taskCodeObj && taskCodeObj.name)
    .map((taskCodeObj) => ({
      value: taskCodeObj.name,
      label: taskCodeObj.name,
    }));
  const users = useSelector((state: RootState) => state.user.users);
  let emailOptions = users
    ? users.map((emailObj) => ({
      value: emailObj.initials,
      label: emailObj.userName,
    }))
    : [];
  const [selectedMatter, setSelectedMatter] = useState(null);
  // console.log("emailOptions", emailOptions);
  const [AccessToken, SetAccessToken] = useState(null);

  
// ...existing code...
useEffect(() => {
  if (formData.isTimeSlipReadOnly && formData.matterts) {
    const matterOption = mattersoptions.find(option => option.label === formData.matterts);
    if (
      (matterOption && (!selectedMatter || selectedMatter.value !== matterOption.value)) ||
      (!matterOption && selectedMatter && selectedMatter.value !== formData.matterts)
    ) {
      setSelectedMatter(matterOption || {
        value: formData.matteridts,
        label: formData.matterts
      });
    }
  }
}, [formData.isTimeSlipReadOnly, formData.matterts, mattersoptions]);

useEffect(() => {
    if (formData.matterts == "") {
      setSelectedMatter(null);
    }
  }, [formData]);
  
  const { getAccessTokenSilently } = useAuth0();

  const setLocalStorageItem = (key, value) => {
    if (typeof value === "object") {
      SetStorageItem(key, JSON.stringify(value));
    } else {
      SetStorageItem(key, value);
    }
  };

  
useEffect(() => {
  if (formData.isTimeSlipReadOnly && formData.matterts) {
    const matterOption = mattersoptions.find(option => option.label === formData.matterts);
    const newSelected = matterOption || { value: formData.matterts, label: formData.matterts };
    if (!selectedMatter || selectedMatter.value !== newSelected.value) {
      setSelectedMatter(newSelected);
    }
  } else if (!formData.isTimeSlipReadOnly && formData.matterts) {
    const matterOption = mattersoptions.find(option => option.label === formData.matterts);
    if (matterOption && (!selectedMatter || selectedMatter.value !== matterOption.value)) {
      setSelectedMatter(matterOption);
    }
  }
}, [formData.isTimeSlipReadOnly, formData.matterts, mattersoptions]);
// ...existing code...

  const getAccessToken = async () => {
    console.log('LogTimesSlipForms.tsx')
    let token;
    try {
      token = await getAccessTokenSilently();
      if (token != null) {
        SetAccessToken(token);
        setAccessTokenDirect(token);
      }
    } catch (exception) { }
    return token;
  };
  const formatDate = (date) => {

    const Emaildate = getStorageItem(LOCALSTORAGE_KEYS.ItemDate);
    if (Emaildate != null) {
      const date1 = new Date(Emaildate);
      const year = date1.getFullYear();
      const month = String(date1.getMonth() + 1).padStart(2, "0"); // Months are zero-based
      const day = String(date1.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    else {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  };
  
  const validateForm = () => {
    let tempErrors: any = {};

    if (!formData.datets) {
      tempErrors.date = "Date is required";
    }

    if (TimeSlipSetting.zeroDurationType == 3) {
      try {
        if (formData.duration.trim() != "") {
          // if(formData.duration.trim().split(".").length == 2){
          //  const _d =  Number(formData.duration.trim());
          // }
          // else {
          //   formData.duration ="";
          //   tempErrors.duration = "Format must be hours.minutes";
          // }
        }
        else {
          if (!formData.duration || formData.duration.trim() == "" || formData.duration == "") {
            tempErrors.duration = `${uiDictionary.Duration} is required`;
          }
        }
      }
      catch (error) {
        formData.duration = "";
        tempErrors.duration = `${uiDictionary.Duration} is required`;
      }


    }

    if (!formData.timekeeper) {
      tempErrors.timekeeper = `${uiDictionary.Timekeeper} is required`;
    }

    if (!formData.matter) {
      tempErrors.matter = `${uiDictionary.Matter} is required`;
    }

    if (!formData.taskCode) {
      tempErrors.taskCode = `${uiDictionary.TaskCode} is required`;
    }

    if (!formData.activityCode) {
      tempErrors.activityCode = `${uiDictionary.ActivityCode} is required`;
    }

    if (!formData.description) {
      tempErrors.description = `${uiDictionary.Description} is required to create ${uiDictionary.Timekeeping}`;
    }

   
    setErrors(tempErrors);

    return Object.keys(tempErrors).length === 0;
  };

  const onCancel = () => {
    setCommunicationSave(false);
    setOnlyTimeSlip(false);
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    
  };
  const handleMatterChange = (value: any) => {
    // Prevent matter changes when in read-only mode
    if (formData.isTimeSlipReadOnly) {
      return;
    }

    setSelectedMatter(value);
    //SetStorageItem("SelectedMatterTs", (value != null) ? value.label : null);
    setFormData((prev: any) => ({
      ...prev,
      matterts: (value != null) ? value.label : null,
      matteridts : (value != null) ? value.value : 0,
      matterts_manually_set: true, // Mark that the user has manually set the TimeSlip matter
    }));
  };
  return (
    <div className="bg-white max-w-2xl w-full">
      {/* <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Log Timeslip to Clarra</h2>
      </div> */}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Billable/Non-Billable Radio */}
        <div className="flex space-x-6">
          <label className="flex items-center">
            <input
              type="radio"
              name="billable"
              checked={formData.billable}
              onChange={() =>
                setFormData((prev) => ({
                  ...prev,
                  billable: true,
                }))
              }
              disabled={formData.isTimeSlipReadOnly}
              className="w-4 h-4 text-blue-600"
            />
            <span className={`ml-2 ${formData.isTimeSlipReadOnly ? 'text-gray-600' : ''}`}>Billable</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="billable"
              checked={!formData.billable}
              onChange={() =>
                setFormData((prev) => ({
                  ...prev,
                  billable: false,
                }))
              }
              disabled={formData.isTimeSlipReadOnly}
              className="w-4 h-4 text-blue-600"
            />
            <span className={`ml-2 ${formData.isTimeSlipReadOnly ? 'text-gray-600' : ''}`}>Non-Billable</span>
          </label>
        </div>

        {/* Date and Duration Row */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm mb-1">
              Date <span className="text-red-500">*</span>
            </label>
            {formData.isTimeSlipReadOnly ? (
              <input
                type="text"
                value={formData.datets || ''}
                disabled={true}
                className="w-full p-1 border rounded bg-gray-100 cursor-not-allowed text-gray-600"
              />
            ) : (
              <input
                type="date"
                value={formData.datets}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    datets: e.target.value,
                  }))
                }
                className="w-full p-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            {expanded.includes('panel2') && formData.createTimeslip && !formData.datets && errors.datets && (
              <p className="text-red-500 text-xs">{errors.datets}</p>
            )}
          </div>
          {TimeSlipSetting && !TimeSlipSetting.disableTimeTracking && (<div>
            <label className="block text-sm mb-1">
              Duration (hr) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.duration}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  duration: e.target.value,
                }))
              }
              placeholder="0.0"
              disabled={formData.isTimeSlipReadOnly}
              className={`w-full p-1 border rounded ${
                formData.isTimeSlipReadOnly
                  ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                  : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
              }`}
            />
            {/* !formData.duration && */}
            {expanded.includes('panel2') && formData.createTimeslip &&
              errors.duration && (
                <p className="text-red-500 text-xs">{errors.duration}</p>
              )
            }
          </div>)}
        </div>

        {/* Timekeeper and Matter Row */}
        {/* <div className="grid grid-cols-2 gap-4"> */}
        <FormControl fullWidth sx={{ marginTop: "10px" }}>
          <AutocompleteInput
             label={uiDictionary.Timekeeper}
            value={formData.timekeeper}
            options={emailOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                timekeeper: value,
              }))
            }
            required
            placeholder={`Select ${uiDictionary.Timekeeper}`}

           disabled={formData.isTimeSlipReadOnly}
          />
          {expanded.includes('panel2') && formData.createTimeslip && !formData.timekeeper && errors.timekeeper && (
            <p className="text-red-500 text-xs">{errors.timekeeper}</p>
          )}
        </FormControl>
        <FormControl fullWidth sx={{ marginTop: "10px" }}>
          <Dropdown
            label={uiDictionary.Matter}
            value={selectedMatter}
            options={mattersoptions}
            onChange={handleMatterChange}
            isMulti={false}
            required
           disabled={formData.isTimeSlipReadOnly}
          />
          
          {expanded.includes('panel2') && formData.createTimeslip && !formData.matterts && errors.matterts && (
            <p className="text-red-500 text-xs">{errors.matterts}</p>
          )}
        </FormControl>
        {/* </div> */}

        <FormControl fullWidth>
          <AutocompleteInput
             label={uiDictionary.ActivityCode}
            value={formData.activityCode}
            options={activityCodeOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                activityCode: value,
              }))
            }
            required
            placeholder={`Select ${uiDictionary.ActivityCode}`}
          disabled={formData.isTimeSlipReadOnly}
          />
          {expanded.includes('panel2') && formData.createTimeslip && !formData.activityCode && errors.activityCode && (
            <p className="text-red-500 text-xs">{errors.activityCode}</p>
          )}
        </FormControl>
        {/* Task Code and Activity Code Row */}
        <FormControl fullWidth sx={{ marginTop: "10px" }}>
          <AutocompleteInput
            label={uiDictionary.TaskCode}
            value={formData.taskCode}
            options={taskCodeOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                taskCode: value,
              }))
            }
            required
            placeholder={`Select ${uiDictionary.TaskCode}`}
          disabled={formData.isTimeSlipReadOnly}
          />
          {expanded.includes('panel2') && formData.createTimeslip && !formData.taskCode && errors.taskCode && (
            <p className="text-red-500 text-xs">{errors.taskCode}</p>
          )}
        </FormControl>
        {/* Description Field */}
        <div>
          <label className="block text-sm mb-1">
            {uiDictionary.Description} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
           disabled={formData.isTimeSlipReadOnly}
            className={`w-full p-1 border rounded ${
              formData.isTimeSlipReadOnly
                ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
          />
          {!formData.description && errors.description && (
            <p className="text-red-500 text-xs">{errors.description}</p>
          )}
        </div>

        {/* Note Field */}
        <div>
          <label className="block text-sm mb-1">{uiDictionary.Note}</label>
          <textarea
            value={formData.note}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                note: e.target.value,
              }))
            }
            placeholder={`Enter your ${uiDictionary.Note} here`}
            rows={4}
            disabled={formData.isTimeSlipReadOnly}
            className={`w-full p-2 border rounded ${
              formData.isTimeSlipReadOnly
                ? 'bg-gray-100 cursor-not-allowed text-gray-600'
                : 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
            }`}
          />
        </div>

      </form>
    </div>
  );
};

export default LogTimeslipForm;
